import { Component, OnDestroy, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ChallengesApiService } from '../../shared/api/challenges-api.service';
import { IdeasApiService } from '../../shared/api/ideas-api.service';
import { AuthService } from '../../shared/auth/auth.service';
import {
  ChallengeListItemDto,
  CreateChallengeRequest,
  UpdateChallengeRequest
} from '../../shared/api/challenges-api.types';
import { IdeaListItemDto } from '../../shared/api/ideas-api.types';
import { LoaderComponent } from '../../shared/ui/loader/loader.component';

@Component({
  selector: 'app-challenges',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './challenges.component.html',
  styleUrl: './challenges.component.css'
})
export class ChallengesComponent implements OnInit, OnDestroy {
  challenges: ChallengeListItemDto[] = [];
  loading = false;
  errorMessage = '';
  showAddModal = false;
  showViewDetailsModal = false;
  selectedChallenge: ChallengeListItemDto | null = null;
  selectedChallengeIdeas: IdeaListItemDto[] = [];
  loadingIdeas = false;
  openMenuId: string | null = null;
  editingChallengeId: string | null = null;
  addForm: FormGroup;
  page = 1;
  pageSize = 20;
  totalCount = 0;

  private readonly destroy$ = new Subject<void>();
  private pendingOpenChallengeId: string | null = null;
  private openingFromQuery = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly challengesApi: ChallengesApiService,
    private readonly ideasApi: IdeasApiService,
    private readonly auth: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.addForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      overview: ['', [Validators.required, Validators.minLength(10)]],
      deadline: ['', Validators.required],
      pointsReward: [100, [Validators.required, Validators.min(1), Validators.max(10000)]]
    });
  }

  ngOnInit(): void {
    this.loadChallenges();

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const open = (params.get('open') ?? '').trim();
      this.pendingOpenChallengeId = open || null;
      this.tryOpenChallengeFromQuery();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadChallenges(): void {
    this.loading = true;
    this.errorMessage = '';
    this.challengesApi
      .getChallenges(this.page, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.challenges = result.items ?? [];
          this.totalCount = result.totalCount ?? 0;
          this.loading = false;
          this.tryOpenChallengeFromQuery();
        },
        error: () => {
          this.errorMessage = 'Failed to load challenges.';
          this.loading = false;
        }
      });
  }

  openAddModal(): void {
    if (!this.canManageChallenges) {
      return;
    }
    this.editingChallengeId = null;
    this.addForm.reset({ title: '', overview: '', deadline: '', pointsReward: 100 });
    this.showAddModal = true;
  }

  openEditModal(c: ChallengeListItemDto): void {
    if (!this.canManageChallenges) {
      return;
    }
    this.closeMenu();
    this.editingChallengeId = c.id;
    this.addForm.reset({
      title: c.title,
      overview: c.overview,
      deadline: this.toDateInput(c.deadlineUtc),
      pointsReward: c.points
    });
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.editingChallengeId = null;
  }

  saveChallenge(): void {
    if (!this.canManageChallenges) {
      this.errorMessage = 'You are not allowed to manage challenges.';
      return;
    }
    if (this.addForm.invalid) {
      this.addForm.markAllAsTouched();
      return;
    }
    const v = this.addForm.value;
    const deadlineUtc = this.toIsoDate(v.deadline);

    if (this.editingChallengeId) {
      const request: UpdateChallengeRequest = {
        title: v.title.trim(),
        overview: v.overview.trim(),
        deadlineUtc,
        points: Number(v.pointsReward) || 0
      };
      this.challengesApi
        .updateChallenge(this.editingChallengeId, request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadChallenges();
            this.closeAddModal();
          },
          error: () => {
            this.errorMessage = 'Failed to update challenge.';
          }
        });
    } else {
      const request: CreateChallengeRequest = {
        title: v.title.trim(),
        overview: v.overview.trim(),
        deadlineUtc,
        points: Number(v.pointsReward) || 0
      };
      this.challengesApi
        .createChallenge(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadChallenges();
            this.closeAddModal();
          },
          error: () => {
            this.errorMessage = 'Failed to create challenge.';
          }
        });
    }
  }

  deleteChallenge(c: ChallengeListItemDto): void {
    if (!this.canManageChallenges) {
      this.errorMessage = 'You are not allowed to manage challenges.';
      return;
    }
    this.closeMenu();
    if (confirm(`Delete "${c.title}"? This cannot be undone.`)) {
      this.challengesApi
        .deleteChallenge(c.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            if (this.selectedChallenge?.id === c.id) {
              this.closeViewDetails();
            }
            this.loadChallenges();
          },
          error: () => {
            this.errorMessage = 'Failed to delete challenge.';
          }
        });
    }
  }

  toggleMenu(c: ChallengeListItemDto): void {
    this.openMenuId = this.openMenuId === c.id ? null : c.id;
  }

  closeMenu(): void {
    this.openMenuId = null;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeMenu();
  }

  openViewDetails(c: ChallengeListItemDto): void {
    this.selectedChallenge = c;
    this.selectedChallengeIdeas = [];
    this.showViewDetailsModal = true;
    this.loadIdeasForChallenge(c.id);
  }

  loadIdeasForChallenge(challengeId: string): void {
    this.loadingIdeas = true;
    this.ideasApi
      .getIdeasWithOptions({ page: 1, pageSize: 50, challengeId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.selectedChallengeIdeas = result.items ?? [];
          this.loadingIdeas = false;
        },
        error: () => {
          this.selectedChallengeIdeas = [];
          this.loadingIdeas = false;
        }
      });
  }

  closeViewDetails(): void {
    this.showViewDetailsModal = false;
    this.selectedChallenge = null;
    this.selectedChallengeIdeas = [];
  }

  private tryOpenChallengeFromQuery(): void {
    if (this.openingFromQuery) return;
    const id = (this.pendingOpenChallengeId ?? '').trim();
    if (!id) return;
    if (this.loading) return;

    this.openingFromQuery = true;
    this.pendingOpenChallengeId = null;

    const found = this.challenges.find((c) => c.id === id);
    if (found) {
      this.openViewDetails(found);
      this.clearOpenQueryParam();
      this.openingFromQuery = false;
      return;
    }

    this.challengesApi
      .getChallengeById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (dto) => {
          const mapped: ChallengeListItemDto = {
            id: dto.id,
            createdAtUtc: dto.createdAtUtc,
            deadlineUtc: dto.deadlineUtc,
            title: dto.title,
            overview: dto.overview,
            points: dto.points,
            reward: dto.reward ?? null,
            ideasCount: 0
          };
          this.openViewDetails(mapped);
          this.clearOpenQueryParam();
          this.openingFromQuery = false;
        },
        error: () => {
          this.openingFromQuery = false;
        }
      });
  }

  private clearOpenQueryParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { open: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private toDateInput(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  }

  private toIsoDate(dateStr: string): string {
    if (!dateStr) return new Date().toISOString();
    return new Date(dateStr + 'T23:59:59.000Z').toISOString();
  }

  formatDeadlineShort(iso: string): string {
    const d = new Date(iso);
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const y = d.getFullYear();
    return `${m}/${day}/${y}`;
  }

  formatDeadline(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = d.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 0) {
      const absDays = Math.abs(diffDays);
      if (absDays < 30) return `Due ${absDays} day${absDays === 1 ? '' : 's'} ago`;
      if (absDays < 365) return `Due ${Math.floor(absDays / 30)} months ago`;
      return `Due about ${Math.floor(absDays / 365)} year${Math.floor(absDays / 365) > 1 ? 's' : ''} ago`;
    }
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays < 7) return `Due in ${diffDays} days`;
    if (diffDays < 30) return `Due in ${Math.floor(diffDays / 7)} weeks`;
    if (diffDays < 365) return `Due in ${Math.floor(diffDays / 30)} months`;
    return `Due in about ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }

  getStatusLabel(status: number): string {
    const labels: Record<number, string> = {
      0: 'Draft',
      1: 'Submitted',
      2: 'Triage',
      3: 'Under Review',
      4: 'Accepted',
      5: 'Rejected',
      6: 'Planned',
      7: 'In Progress',
      8: 'Delivered',
      9: 'Archived',
      10: 'Need more info'
    };
    return labels[status] ?? 'Unknown';
  }

  getStatusClass(status: number): string {
    const classes: Record<number, string> = {
      0: 'tag-draft',
      1: 'tag-submitted',
      2: 'tag-triage',
      3: 'tag-under-review',
      4: 'tag-approved',
      5: 'tag-rejected',
      6: 'tag-planned',
      7: 'tag-in-progress',
      8: 'tag-delivered',
      9: 'tag-archived',
      10: 'tag-need-info'
    };
    return classes[status] ?? 'tag-default';
  }

  formatIdeaDate(iso: string): string {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 365) return `about ${Math.floor(diffDays / 30)} months ago`;
    return `about ${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
  }

  get canManageChallenges(): boolean {
    const roles = this.auth.backendRoles;
    return roles.some((r) => {
      const code = r.toUpperCase();
      return code === 'ADMIN' || code === 'COMMITTEE' || code === 'MODERATOR';
    });
  }
}
